/**
 * シーン単位のフレーム生成設定 (追加プロンプト・参照画像の抜き差し) の保存キュー。
 * docs/features/scene-frame-generation-tuning-plan.md §6.7。
 *
 * - 設定の正本は保存済みの scenes 行。ここはその「保存の順序」と「最新の保存済み値」を管理する。
 * - シーン単位で直列化する: 前の保存が終わるまで次を送らない (連続変更で順序が入れ替わらない)。
 *   未送信の差分は 1 つに畳み込む (最後の値が勝つ)。
 * - flush は保留中・送信中の保存をすべて待ち、最新の保存済み設定を返す。生成要求はこの戻り値から組む。
 * - 失敗した差分は未送信に戻し自動では再送しない (retry で送り直す)。flush は失敗を投げる。
 * - reset は未送信を破棄し、送信中の完了を待ってから「未設定」を保存する (送信済みは取り消せないため順序で解決)。
 */
import type { SceneFrameReferenceOverrides } from '../../../../features/scenarios/types';

export interface SceneFrameSettings {
  /** 追加プロンプト。未設定は '' */
  additionalPrompt: string;
  /** 参照画像の抜き差し。未設定は null */
  overrides: SceneFrameReferenceOverrides | null;
}

export type SceneFrameSettingsPatch = Partial<SceneFrameSettings>;

export const EMPTY_SCENE_FRAME_SETTINGS: SceneFrameSettings = Object.freeze({ additionalPrompt: '', overrides: null });

export type SceneFrameSettingsSaver = (sceneId: string, patch: SceneFrameSettingsPatch) => Promise<void>;

/** save に渡す差分。関数なら「意図した最新値」(保存済み + 送信中 + 未送信) を受けて差分を返す
 *  (ピッカーで A→B と続けて追加したとき、B の保存が A を上書きしないように)。 */
export type SceneFrameSettingsUpdate = SceneFrameSettingsPatch | ((current: SceneFrameSettings) => SceneFrameSettingsPatch);

export class SceneFrameSettingsQueue {
  private saved = new Map<string, SceneFrameSettings>();
  private inFlight = new Map<string, Promise<void>>();
  private pending = new Map<string, SceneFrameSettingsPatch>();
  /** 送信中の差分 (意図した最新値の計算に使う) */
  private sending = new Map<string, SceneFrameSettingsPatch>();
  private lastError = new Map<string, unknown>();
  private readonly saver: SceneFrameSettingsSaver;

  constructor(saver: SceneFrameSettingsSaver) {
    this.saver = saver;
  }

  /** scenes 行から読んだ値を「保存済み」として登録する (既に登録済みなら何もしない) */
  seedIfAbsent(sceneId: string, settings: SceneFrameSettings): void {
    if (!this.saved.has(sceneId)) this.saved.set(sceneId, { ...settings });
  }

  /** 最新の保存済み設定 (未登録なら空) */
  get(sceneId: string): SceneFrameSettings {
    return this.saved.get(sceneId) ?? EMPTY_SCENE_FRAME_SETTINGS;
  }

  /** 意図した最新値 = 保存済み + 送信中の差分 + 未送信の差分 (関数型の save の入力) */
  getIntended(sceneId: string): SceneFrameSettings {
    return { ...this.get(sceneId), ...(this.sending.get(sceneId) ?? {}), ...(this.pending.get(sceneId) ?? {}) };
  }

  /** 未送信・送信中の差分があるか */
  hasUnsaved(sceneId: string): boolean {
    return this.pending.has(sceneId) || this.inFlight.has(sceneId);
  }

  /** 直近の保存失敗 (無ければ undefined) */
  getError(sceneId: string): unknown {
    return this.lastError.get(sceneId);
  }

  /** 未送信の差分 (無ければ null)。保存失敗で戻された差分もここに残る (ダイアログの再表示で復元する) */
  getPending(sceneId: string): SceneFrameSettingsPatch | null {
    return this.pending.get(sceneId) ?? null;
  }

  /** 未送信の差分と保存失敗を破棄する (送信中のものは触らない)。復旧できない入力を捨てて生成を再開できるようにする */
  discardPending(sceneId: string): void {
    this.pending.delete(sceneId);
    this.lastError.delete(sceneId);
  }

  /** 差分を積んで保存する。この差分が保存されるまで待ち、失敗なら投げる。
   *  関数を渡すと、意図した最新値 (保存済み + 送信中 + 未送信) に対して差分を作る */
  save(sceneId: string, update: SceneFrameSettingsUpdate): Promise<SceneFrameSettings> {
    const patch = typeof update === 'function' ? update(this.getIntended(sceneId)) : update;
    this.pending.set(sceneId, { ...(this.pending.get(sceneId) ?? {}), ...patch });
    this.lastError.delete(sceneId);
    this.kick(sceneId);
    return this.flush(sceneId);
  }

  /** 保留中・送信中の保存をすべて待ち、最新の保存済み設定を返す。保存に失敗していれば投げる */
  async flush(sceneId: string): Promise<SceneFrameSettings> {
    while (this.inFlight.has(sceneId)) {
      await this.inFlight.get(sceneId);
    }
    if (this.lastError.has(sceneId)) throw this.lastError.get(sceneId);
    return this.get(sceneId);
  }

  /** 失敗した差分を送り直す */
  retry(sceneId: string): Promise<SceneFrameSettings> {
    this.lastError.delete(sceneId);
    this.kick(sceneId);
    return this.flush(sceneId);
  }

  /** 未送信を破棄し、送信中の完了を待ってから「未設定」を保存する */
  async reset(sceneId: string): Promise<SceneFrameSettings> {
    this.pending.delete(sceneId);
    this.lastError.delete(sceneId);
    while (this.inFlight.has(sceneId)) {
      await this.inFlight.get(sceneId)!.catch(() => undefined);
    }
    return this.save(sceneId, { ...EMPTY_SCENE_FRAME_SETTINGS });
  }

  private kick(sceneId: string): void {
    if (this.inFlight.has(sceneId)) return;
    const patch = this.pending.get(sceneId);
    if (!patch) return;
    this.pending.delete(sceneId);
    this.sending.set(sceneId, patch);
    const run = this.saver(sceneId, patch).then(
      () => {
        this.saved.set(sceneId, { ...this.get(sceneId), ...patch });
      },
      (err: unknown) => {
        this.lastError.set(sceneId, err);
        // 失敗した差分は未送信に戻す (後から積まれた差分が優先)。自動では再送しない
        this.pending.set(sceneId, { ...patch, ...(this.pending.get(sceneId) ?? {}) });
      },
    ).finally(() => {
      this.sending.delete(sceneId);
      this.inFlight.delete(sceneId);
      if (!this.lastError.has(sceneId)) this.kick(sceneId);
    });
    this.inFlight.set(sceneId, run);
  }
}

/** scenes 行から設定を読む */
export function sceneFrameSettingsFromScene(scene: {
  frame_additional_prompt?: string | null;
  frame_reference_overrides?: SceneFrameReferenceOverrides | null;
}): SceneFrameSettings {
  return {
    additionalPrompt: scene.frame_additional_prompt ?? '',
    overrides: scene.frame_reference_overrides ?? null,
  };
}

/** 設定が 1 つでも入っているか (「調整」ボタンの色分け用) */
export function hasSceneFrameSettings(settings: SceneFrameSettings): boolean {
  if (settings.additionalPrompt.trim()) return true;
  const o = settings.overrides;
  return !!o && ((o.exclude?.length ?? 0) + (o.include?.length ?? 0) + (o.added?.length ?? 0)) > 0;
}
